import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const RELOAD_FLAG_KEY = 'sm_chunk_reload_attempt';

const isChunkLoadError = (err: unknown): boolean => {
  if (!err) return false;
  const name = (err as Error)?.name || '';
  const message = (err as Error)?.message || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk \S+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
};

class ErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) {
      // Stale bundle reference (an old service-worker / browser cache pointed at
      // chunk filenames that no longer exist on the new deploy). One-shot reload
      // pulls fresh assets. We use a sessionStorage flag so we don't loop forever
      // if reloading doesn't fix it.
      let attempted = false;
      try {
        attempted = sessionStorage.getItem(RELOAD_FLAG_KEY) === '1';
      } catch {
        // sessionStorage may be unavailable in private mode
      }
      if (!attempted) {
        try {
          sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
        } catch {}
        const reload = () => window.location.reload();
        // Also drop SW caches so the next load is truly fresh.
        const w = window as any;
        if (w.caches) {
          w.caches.keys().then((keys: string[]) =>
            Promise.all(keys.map((k: string) => w.caches.delete(k)))
          ).finally(reload);
        } else {
          reload();
        }
        return;
      }
      // Already tried reloading - clear the flag so a future genuine error
      // doesn't get swallowed and fall through to the UI.
      try {
        sessionStorage.removeItem(RELOAD_FLAG_KEY);
      } catch {}
    }
    console.error('Uncaught error:', error, errorInfo);
  }

  private onManualReload = () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG_KEY);
    } catch {}
    const reload = () => window.location.reload();
    const w = window as any;
    if (w.caches) {
      w.caches.keys().then((keys: string[]) =>
        Promise.all(keys.map((k: string) => w.caches.delete(k)))
      ).finally(reload);
    } else {
      reload();
    }
  };

  public render() {
    if (this.state.hasError) {
      const chunkIssue = isChunkLoadError(this.state.error);
      return (
        <div className="min-h-screen flex items-center justify-center bg-off-white px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-coral/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl">⚠️</span>
            </div>
            <h1 className="font-nunito font-bold text-2xl text-charcoal mb-4">
              {chunkIssue ? 'A new version is available' : 'Something went wrong'}
            </h1>
            <p className="text-gray-600 mb-8">
              {chunkIssue
                ? 'We updated the site. Refresh once to load the latest version.'
                : "We're sorry for the inconvenience. Please try refreshing the page."}
            </p>
            <button
              onClick={this.onManualReload}
              className="bg-coral hover:bg-coral/90 text-white font-semibold px-6 py-3 rounded-2xl transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
